/**
 * ML Noise Suppressor - TensorFlow.js-based Real-Time Audio Enhancement
 *
 * Integrates trained ML model for noise suppression
 * Falls back to traditional DSP if ML is unavailable
 */

import * as tf from "@tensorflow/tfjs";

// =============================================================================
// L2 regularizer compatibility
//
// Some exported Keras model.json files include:
//   "kernel_regularizer": {"class_name": "L2", ...}
// TF.js deserialization can fail if "L2" is not registered in this runtime.
// For inference-only usage, this regularizer has no behavioral impact.
// =============================================================================
class L2RegularizerCompat {
  static className = "L2";

  private readonly l2: number;

  constructor(config?: {l2?: number}) {
    this.l2 = config?.l2 ?? 0;
  }

  apply(x: tf.Tensor): tf.Scalar {
    // Keep mathematically correct implementation for compatibility.
    return tf.tidy(
      () => tf.sum(tf.square(x)).mul(tf.scalar(this.l2)) as tf.Scalar
    );
  }

  getConfig(): tf.serialization.ConfigDict {
    return {l2: this.l2};
  }
}
tf.serialization.registerClass(L2RegularizerCompat as any);

// =============================================================================
// Custom GRUCell — supports reset_after=True (Keras 2.x default)
//
// TF.js 4.x throws ValueError for reset_after=True in its built-in GRUCell.
// We register a replacement class BEFORE loadLayersModel is called.
// It implements both paths so reset_after=false models also work correctly.
// =============================================================================
class GRUCellResetAfterSupport extends tf.layers.Layer {
  // Must match TF.js serialization class name
  static className = "GRUCell";

  private readonly units: number;
  private readonly resetAfter: boolean;
  private readonly useBiasFlag: boolean;
  private readonly activationFn: (x: tf.Tensor) => tf.Tensor;
  private readonly recurrentActivationFn: (x: tf.Tensor) => tf.Tensor;

  // Weight variables — assigned in build()
  private kernelW!: tf.LayerVariable;
  private recurrentKernelW!: tf.LayerVariable;
  private biasW?: tf.LayerVariable;

  // Required by TF.js RNN infra
  public stateSize: number;
  public dropoutMask: tf.Tensor | tf.Tensor[] | null = null;
  public recurrentDropoutMask: tf.Tensor | tf.Tensor[] | null = null;

  constructor(config: any) {
    super(config);
    this.units = config.units as number;
    // Accept BOTH camelCase (TF.js internal) and snake_case (Keras JSON export)
    this.resetAfter = (config.resetAfter ?? config.reset_after) !== false; // Keras default = true
    this.useBiasFlag = (config.useBias ?? config.use_bias) !== false;
    this.stateSize = this.units;
    this.activationFn = GRUCellResetAfterSupport.makeActivation(
      config.activation || "tanh"
    );
    this.recurrentActivationFn = GRUCellResetAfterSupport.makeActivation(
      config.recurrentActivation ?? config.recurrent_activation ?? "sigmoid" // Keras default (model.json uses 'sigmoid', NOT 'hardSigmoid')
    );
  }

  override build(inputShape: tf.Shape | tf.Shape[]): void {
    // TF.js may pass inputShape in two forms depending on version:
    //   (a) [[null, features], [null, units], …]  — array of shapes (one per input+state)
    //   (b) [null, features]                       — flat step-input shape directly
    // We need the LAST element of the step-input shape as inputDim.
    // Detect form by checking whether the first element is itself an array.
    let shape: tf.Shape;
    if (Array.isArray((inputShape as tf.Shape[])[0])) {
      // Form (a): first element is the step-input shape, e.g. [null, 40]
      shape = (inputShape as tf.Shape[])[0] as tf.Shape;
    } else {
      // Form (b): inputShape IS the step-input shape, e.g. [null, 40]
      shape = inputShape as tf.Shape;
    }
    const inputDim = (shape as number[])[shape.length - 1] as number;

    this.kernelW = this.addWeight(
      "kernel",
      [inputDim, this.units * 3],
      "float32",
      tf.initializers.glorotUniform({})
    );
    this.recurrentKernelW = this.addWeight(
      "recurrent_kernel",
      [this.units, this.units * 3],
      "float32",
      tf.initializers.orthogonal({})
    );
    if (this.useBiasFlag) {
      // reset_after=True  → bias shape [2, 3*units] (kernel row + recurrent row)
      // reset_after=False → bias shape [3*units]
      const biasShape = this.resetAfter
        ? [2, this.units * 3]
        : [this.units * 3];
      this.biasW = this.addWeight(
        "bias",
        biasShape as number[],
        "float32",
        tf.initializers.zeros()
      );
    }
    this.built = true;
  }

  override call(
    inputs: tf.Tensor[],
    _kwargs: Record<string, unknown>
  ): tf.Tensor[] {
    return tf.tidy(() => {
      const input = inputs[0];
      const hPrev = inputs[1];
      let matrixX = input.matMul(this.kernelW.read());

      if (this.resetAfter) {
        // ── reset_after=True (Keras default) ──────────────────────────────────
        // Bias is [2, 3*units]: row 0 = kernel bias, row 1 = recurrent bias
        let bKernel: tf.Tensor | null = null;
        let bRecurrent: tf.Tensor | null = null;
        if (this.useBiasFlag && this.biasW) {
          const b = this.biasW.read(); // [2, 3*units]
          bKernel = b.slice([0, 0], [1, -1]).squeeze([0]); // [3*units]
          bRecurrent = b.slice([1, 0], [1, -1]).squeeze([0]); // [3*units]
        }
        if (bKernel) matrixX = matrixX.add(bKernel);

        // Full recurrent multiply for all gates
        const matrixInner = hPrev.matMul(this.recurrentKernelW.read()); // [B, 3u]
        const [xZ, xR, xH] = tf.split(matrixX, 3, matrixX.rank - 1);
        const [iZ, iR, iH] = tf.split(matrixInner, 3, matrixInner.rank - 1);

        let rZ = iZ,
          rR = iR,
          rH = iH;
        if (bRecurrent) {
          const [rbZ, rbR, rbH] = tf.split(bRecurrent, 3, 0);
          rZ = iZ.add(rbZ);
          rR = iR.add(rbR);
          rH = iH.add(rbH); // recurrent bias applied before r gate
        }

        const z = this.recurrentActivationFn(xZ.add(rZ));
        const r = this.recurrentActivationFn(xR.add(rR));
        // Key difference: r gates the recurrent part AFTER kernel multiply
        const hh = this.activationFn(xH.add(r.mul(rH)));
        const h = tf.add(tf.mul(z, hPrev), tf.mul(tf.sub(tf.scalar(1), z), hh));
        return [h, h];
      } else {
        // ── reset_after=False (TF.js default) ────────────────────────────────
        if (this.useBiasFlag && this.biasW) {
          matrixX = matrixX.add(this.biasW.read());
        }
        const rk = this.recurrentKernelW.read();
        const [rk1, rk2] = tf.split(
          rk,
          [2 * this.units, this.units],
          rk.rank - 1
        );
        const matrixInner = hPrev.matMul(rk1);
        const [xZ, xR, xH] = tf.split(matrixX, 3, matrixX.rank - 1);
        const [recZ, recR] = tf.split(matrixInner, 2, matrixInner.rank - 1);
        const z = this.recurrentActivationFn(xZ.add(recZ));
        const r = this.recurrentActivationFn(xR.add(recR));
        const recH = tf.mul(r, hPrev).matMul(rk2);
        const hh = this.activationFn(xH.add(recH));
        const h = tf.add(tf.mul(z, hPrev), tf.mul(tf.sub(tf.scalar(1), z), hh));
        return [h, h];
      }
    });
  }

  private static makeActivation(name: string): (x: tf.Tensor) => tf.Tensor {
    switch (name.toLowerCase()) {
      case "tanh":
        return (x) => x.tanh();
      case "sigmoid":
        return (x) => x.sigmoid();
      case "hardsigmoid":
      case "hard_sigmoid":
        return (x) =>
          tf.clipByValue(x.mul(tf.scalar(0.2)).add(tf.scalar(0.5)), 0, 1);
      case "relu":
        return (x) => x.relu();
      case "linear":
        return (x) => x;
      default:
        return (x) => x.sigmoid();
    }
  }

  override getConfig(): tf.serialization.ConfigDict {
    return {
      ...super.getConfig(),
      units: this.units,
      useBias: this.useBiasFlag,
      resetAfter: this.resetAfter,
    };
  }
}
// Override TF.js built-in GRUCell — must happen before loadLayersModel
// (Kept as a safety net for any direct GRUCell deserialization paths.)
tf.serialization.registerClass(GRUCellResetAfterSupport);

// =============================================================================
// GRULayerWithResetAfter — intercepts GRU LAYER deserialization
//
// WHY registerClass('GRUCell') alone is NOT enough:
//   TF.js built-in GRU layer constructor does `new GRUCell(args)` DIRECTLY —
//   it never goes through the serialization registry.  So our custom GRUCell
//   never gets instantiated.  We must register a replacement for the GRU LAYER
//   itself so that when model.json contains "class_name": "GRU", TF.js calls
//   GRULayerWithResetAfter.fromConfig() instead of the built-in GRU constructor.
//
// WEIGHT NAMING:
//   model.json weightsManifest uses paths like  "gru_96/gru_cell/kernel".
//   TF.js builds variable names as  `${layer.name}/${varName}`.
//   So the cell MUST be named `${layerName}/gru_cell`  (e.g. "gru_96/gru_cell")
//   so that addWeight('kernel') → "gru_96/gru_cell/kernel"  ← matches manifest.
// =============================================================================
class GRULayerWithResetAfter {
  static className = "GRU";

  static fromConfig(_cls: unknown, config: any): tf.layers.Layer {
    const layerName: string = config.name as string;

    // Create our cell — named so its weights match the manifest paths.
    // IMPORTANT: name must be just "gru_cell" (no layer prefix).
    // The RNN wrapper layer is already named `layerName` (e.g. "gru_96").
    // TF.js scopes cell weights under the RNN layer scope, so the final path
    // becomes "gru_96/gru_cell/kernel" ✅  (matches the weightsManifest).
    // Using `${layerName}/gru_cell` would double-prefix → "gru_96/gru_96/gru_cell/kernel" ❌
    const cell = new GRUCellResetAfterSupport({
      ...config,
      name: `gru_cell`, // → weights: "gru_96/gru_cell/kernel" ✅
      // Normalise keys: Keras JSON is snake_case; TF.js internals are camelCase
      useBias: config.useBias ?? config.use_bias ?? true,
      recurrentActivation:
        config.recurrentActivation ?? config.recurrent_activation ?? "sigmoid",
      resetAfter: (config.resetAfter ?? config.reset_after) !== false,
      returnSequences:
        config.returnSequences ?? config.return_sequences ?? false,
    });

    // Wrap in a standard RNN layer — it exposes cell.trainableWeights correctly
    return tf.layers.rnn({
      cell: cell as any,
      returnSequences:
        ((config.returnSequences ?? config.return_sequences) as boolean) ??
        false,
      returnState:
        ((config.returnState ?? config.return_state) as boolean) ?? false,
      goBackwards:
        ((config.goBackwards ?? config.go_backwards) as boolean) ?? false,
      stateful: (config.stateful as boolean) ?? false,
      unroll: (config.unroll as boolean) ?? false,
      // Always use float32 — model.json exports dtype as a Keras Policy object
      // (e.g. {class_name:"Policy", config:{name:"mixed_float16"}}) which TF.js
      // cannot use as a DataType string. Weights are float32 anyway (UINT8 quantized
      // with original_dtype=float32), so float32 is always correct here.
      dtype: "float32" as tf.DataType,
      name: layerName, // e.g. "gru_96" — RNN layer keeps this name
    }) as tf.layers.Layer;
  }
}
tf.serialization.registerClass(GRULayerWithResetAfter as any);

export interface ModelConfig {
  model_name: string;
  sample_rate: number;
  frame_size: number;
  hop_length: number;
  n_fft?: number; // FFT size for mel spectrogram (default 2048)
  sequence_length: number;
  n_mels: number;
  quantized: boolean;
  voice_frequency_ranges?: {
    fundamental: number[];
    core_pitch: number[];
    warmth: number[];
  };
}

interface NormalizationStats {
  mean: number;
  std: number;
}

// WebRTC standard sample rate (browser audio always comes in at this rate)
const WEBRTC_SAMPLE_RATE = 48000;

// ─── Public handle interface ──────────────────────────────────────────────────

export interface MLNoiseSuppressorHandle {
  initialize(modelUrl: string): Promise<void>;
  isReady(): boolean;
  getModelConfig(): ModelConfig | null;
  getInfo(): {initialized: boolean; backend: string; modelLoaded: boolean};
  computeGainsFromFeatures(
    features: Float32Array,
    seqLen: number,
    nMels: number,
    participantId?: string
  ): Promise<Float32Array>;
  clearParticipantSmoothingState(participantId: string): void;
  processAudioSync(inputBuffer: Float32Array): Float32Array;
  reset(): void;
  dispose(): void;
}

export function createMLNoiseSuppressor(): MLNoiseSuppressorHandle {
  const instance = new MLNoiseSuppressor();
  return {
    initialize: (url) => instance.initialize(url),
    isReady: () => instance.isReady(),
    getModelConfig: () => instance.getModelConfig(),
    getInfo: () => instance.getInfo(),
    computeGainsFromFeatures: (f, s, n, p) =>
      instance.computeGainsFromFeatures(f, s, n, p),
    clearParticipantSmoothingState: (id) =>
      instance.clearParticipantSmoothingState(id),
    processAudioSync: (buf) => instance.processAudioSync(buf),
    reset: () => instance.reset(),
    dispose: () => instance.dispose(),
  };
}

/** @internal — keep as class; TF.js serialization cannot use plain objects */
export class MLNoiseSuppressor {
  private model: tf.LayersModel | null = null;
  private config: ModelConfig | null = null;
  private normStats: NormalizationStats | null = null;
  private isInitialized = false;

  // Temporal smoothing state — per-participant to avoid cross-participant corruption
  // when multiple async computeGainsFromFeatures() calls run concurrently.
  private prevMaskMap: Map<string, Float32Array> = new Map();
  // Faster smoothing: 0.70 means 30% of new value per frame (~3 frames to stabilise).
  // Old 0.92 was too slow — first speech frame had smoothed max≈0.08 which fell below
  // the isSpeechFrame threshold and silenced the first ~200ms of every utterance.
  private readonly SMOOTHING_ALPHA = 0.7;

  // Mel filterbank cache (built once, reused every frame)
  private melFilterbank: Float32Array[] | null = null;

  // ── Google Meet-style ring buffer architecture ─────────────────────────────
  // Process audio in exact hop_length (80 sample) steps at 16kHz.
  // Each hop: STFT → apply cached FFT gains → ISTFT → OLA.
  // Model runs async, updates cachedFftGains after each inference.
  // Audio thread cost: ~2ms. Model inference: async, never blocks.

  // Persistent input ring buffer (16kHz) — accumulates samples across callbacks.
  private inRing: Float32Array = new Float32Array(8192);
  private inRingLen: number = 0;

  // OLA accumulator (16kHz) — each hop's ISTFT output is overlap-added here.
  private outAccum: Float32Array = new Float32Array(16384);
  private outNorm: Float32Array = new Float32Array(16384); // sum of hann² for normalisation

  // Per-FFT-bin gains from last completed async inference.
  // All-ones until first inference completes → transparent passthrough.
  private cachedFftGains: Float32Array | null = null;
  private inferenceInFlight = false;

  // Hann window cache (allocated once per N_FFT)
  private hannWinCache: Float32Array | null = null;

  // Rolling mel frame history — seq_len frames of mel features for model input.
  private melFrameHistory: number[][] = [];

  // Resampling state (for 16kHz models)
  private needsResampling = false;
  private resampleRatio = 1; // WEBRTC_SAMPLE_RATE / model.sample_rate

  // Legacy fields (kept for isReady / reset API compatibility)
  private lastComplexFrames: {real: Float32Array; imag: Float32Array}[] = [];
  private readonly VOICE_FUNDAMENTAL_MIN = 80;
  private readonly VOICE_FUNDAMENTAL_MAX = 500;
  private frameCounter = 0;
  private skipFrames = 0;
  private lastProcessedOutput: Float32Array | null = null;
  private processingTimes: number[] = [];
  private readonly MAX_PROCESSING_TIME_MS = 42;

  /**
   * Initialize ML model for noise suppression
   * @param modelUrl Path to model.json file
   */
  async initialize(modelUrl: string): Promise<void> {
    try {
      // Use CPU backend — for small 73K-param GRU models, CPU is faster than WebGL.
      // WebGL has shader compilation + GPU↔CPU transfer overhead (~25-30ms) that dwarfs
      // actual compute time (~2-5ms). CPU backend runs inference in <5ms consistently.
      try {
        await tf.setBackend("cpu");
      } catch {
        await tf.setBackend("webgl");
      }
      await tf.ready();
      console.log(
        `[MLNoiseSuppressor] TF.js backend ready: ${tf.getBackend()}`
      );

      // ── Weight remapping fix ─────────────────────────────────────────────────
      // model.json manifest uses "gru_96/gru_cell/kernel" (Keras scoping).
      // TF.js 4.x variables are named "gru_96/kernel" (no gru_cell/ scope).
      // loadLayersModel(url) fails to match → GRU stays at glorot_uniform → rawMax≈0.
      //
      // Fix: fetch topology + binary ourselves, strip "/gru_cell/" from weightSpecs,
      // then load via tf.io.fromMemory so TF.js uses correct names with correct byte offsets.
      const baseUrlForWeights = modelUrl
        .split("?")[0]
        .replace(/model\.json$/, "");
      const [modelJsonResp, binResp] = await Promise.all([
        fetch(modelUrl),
        fetch(`${baseUrlForWeights}group1-shard1of1.bin`),
      ]);
      const modelJsonData = (await modelJsonResp.json()) as {
        modelTopology: unknown;
        weightsManifest: Array<{
          paths: string[];
          weights: Array<{
            name: string;
            shape: number[];
            dtype: string;
            quantization?: unknown;
          }>;
        }>;
        format?: string;
        generatedBy?: string;
        convertedBy?: string;
      };
      const weightData = await binResp.arrayBuffer();

      // Strip "/gru_cell/" from each weight name — keeps byte offsets intact
      const remappedWeightSpecs = modelJsonData.weightsManifest[0].weights.map(
        (w) => ({...w, name: w.name.replace("/gru_cell/", "/")})
      );

      this.model = await tf.loadLayersModel(
        tf.io.fromMemory({
          modelTopology: modelJsonData.modelTopology,
          weightSpecs: remappedWeightSpecs,
          weightData: weightData,
          format: modelJsonData.format,
          generatedBy: modelJsonData.generatedBy,
          convertedBy: modelJsonData.convertedBy,
        } as tf.io.ModelArtifacts)
      );
      console.log(
        `[MLNoiseSuppressor] Model loaded — ${this.model.countParams().toLocaleString()} params | backend: ${tf.getBackend()} | url: ${modelUrl}`
      );

      // Load config
      const baseUrl = modelUrl.substring(0, modelUrl.lastIndexOf("/"));
      const configResponse = await fetch(`${baseUrl}/model_config.json`);
      this.config = await configResponse.json();

      // Load normalization stats
      try {
        const normResponse = await fetch(`${baseUrl}/normalization_stats.json`);
        this.normStats = await normResponse.json();
        console.log(
          `[MLNoiseSuppressor] Normalization stats loaded — mean: ${this.normStats!.mean.toFixed(4)}, std: ${this.normStats!.std.toFixed(4)}`
        );
      } catch (e) {
        console.warn(
          `[MLNoiseSuppressor] normalization_stats.json not found, using defaults (mean=0, std=1)`
        );
        this.normStats = {mean: 0, std: 1};
      }

      // Check if resampling is needed (model trained at different sample rate than WebRTC)
      const modelSampleRate = this.config!.sample_rate || 48000;
      if (modelSampleRate !== WEBRTC_SAMPLE_RATE) {
        this.needsResampling = true;
        this.resampleRatio = WEBRTC_SAMPLE_RATE / modelSampleRate;
        console.log(
          `[MLNoiseSuppressor] Resampling enabled: ${WEBRTC_SAMPLE_RATE}Hz → ${modelSampleRate}Hz (ratio: ${this.resampleRatio})`
        );
      } else {
        this.needsResampling = false;
        this.resampleRatio = 1;
      }

      // Warmup: run 3 dummy inferences to JIT-compile GRU + dense paths.
      // Without warmup, TF.js compiles lazily on the audio thread → first ~20 frames are slow.
      const seqLen = this.config!.sequence_length || 8;
      const nMels = this.config!.n_mels || 40;
      console.log(
        `[MLNoiseSuppressor] Warming up model (${seqLen} × ${nMels})...`
      );
      const warmupInput = tf.zeros([1, seqLen, nMels]);
      for (let w = 0; w < 3; w++) {
        const warmupOut = this.model!.predict(warmupInput) as tf.Tensor;
        warmupOut.dataSync(); // force full synchronous execution
        warmupOut.dispose();
      }
      warmupInput.dispose();

      // Weight-load diagnostic: run model on constant 0.5 input to verify GRU weights loaded.
      // If weights loaded correctly, output should be non-trivial (max > 0.01).
      // If max ≈ 0.0000 the GRU weights failed to load (cell naming mismatch).
      const diagInput = tf.fill([1, seqLen, nMels], 0.5);
      const diagOut = this.model!.predict(diagInput) as tf.Tensor;
      const diagData = new Float32Array(await diagOut.dataSync());
      const diagMax = Math.max(...Array.from(diagData));
      const diagMin = Math.min(...Array.from(diagData));
      diagOut.dispose();
      diagInput.dispose();
      console.log(
        `[MLNoiseSuppressor] ⚠️ Weight check (input=0.5): output max=${diagMax.toFixed(4)} min=${diagMin.toFixed(4)} — ${diagMax > 0.01 ? "✅ weights LOADED" : "❌ weights NOT loaded (all-zero output)"}`
      );

      // Dump all TF.js weight variable names so we can compare against the weightsManifest.
      // The manifest has paths like "gru_96/gru_cell/kernel".
      // If TF.js names the variable differently, the weight won't load (model outputs ~0).
      console.log(
        `[MLNoiseSuppressor] 📋 TF.js weight variable names (${this.model!.weights.length} total):`
      );
      this.model!.weights.forEach((w, i) => {
        const vals = w.read().dataSync();
        const wMax = Math.max(...Array.from(vals).slice(0, 20)); // first 20 values
        const wMin = Math.min(...Array.from(vals).slice(0, 20));
        console.log(
          `  [${i}] ${w.name}  shape=${JSON.stringify(w.shape)}  val_range=[${wMin.toFixed(4)}, ${wMax.toFixed(4)}]${Math.abs(wMax) < 1e-6 && Math.abs(wMin) < 1e-6 ? " ← ❌ ALL ZEROS (not loaded!)" : ""}`
        );
      });

      console.log(`[MLNoiseSuppressor] Warmup done`);

      this.isInitialized = true;
      console.log(`[MLNoiseSuppressor] ✅ Ready — noise suppression is ACTIVE`);
      console.log(
        `[MLNoiseSuppressor] Config: ${modelSampleRate}Hz, ${this.config!.n_mels} mels, n_fft=${this.config!.n_fft || 2048}`
      );
    } catch (error) {
      console.error(`[MLNoiseSuppressor] ❌ Initialization failed:`, error);
      throw error;
    }
  }

  /**
   * Google Meet-style ring buffer noise suppression.
   *
   * Trains / Google Meet process audio in exact hop_length steps (80 samples = 5ms @ 16kHz).
   * Each step:
   *   1. Extract one STFT frame (n_fft=512) with Hann window — zero-pad if near end
   *   2. Apply cached per-FFT-bin gains (from last async inference) → ISTFT
   *   3. Overlap-add into persistent output ring buffer
   *   4. Fire async inference (non-blocking) to update gains for next steps
   *
   * Audio thread cost: ~2ms (pure JS STFT + OLA).
   * Model cost: async, never blocks audio thread.
   * Mask staleness: ≤1 hop (5ms) — imperceptible for noise suppression.
   */
  processAudioSync(inputBuffer: Float32Array): Float32Array {
    if (!this.isInitialized || !this.model || !this.config) {
      return inputBuffer;
    }

    const N_FFT = this.config.n_fft || 512;
    const HOP = this.config.hop_length || 80;
    const N_MELS = this.config.n_mels || 40;
    const SEQ_LEN = this.config.sequence_length || 8;
    const bins = N_FFT / 2 + 1;

    // Hann window — allocated once
    if (!this.hannWinCache || this.hannWinCache.length !== N_FFT) {
      this.hannWinCache = new Float32Array(N_FFT);
      for (let i = 0; i < N_FFT; i++)
        this.hannWinCache[i] =
          0.5 * (1 - Math.cos((2 * Math.PI * i) / (N_FFT - 1)));
    }
    const hann = this.hannWinCache;

    try {
      // ── 1. Downsample 48kHz → 16kHz ─────────────────────────────────────────
      const in16 = this.needsResampling
        ? this.downsample(inputBuffer, this.resampleRatio)
        : inputBuffer;

      // ── 2. Append to input ring buffer ──────────────────────────────────────
      if (this.inRingLen + in16.length > this.inRing.length) {
        const grown = new Float32Array(
          Math.max((this.inRingLen + in16.length) * 2, 8192)
        );
        grown.set(this.inRing.subarray(0, this.inRingLen));
        this.inRing = grown;
      }
      this.inRing.set(in16, this.inRingLen);
      this.inRingLen += in16.length;

      // Grow output buffers if needed (each hop can write up to offset + N_FFT)
      const maxOut = this.inRingLen + N_FFT;
      if (maxOut > this.outAccum.length) {
        const g1 = new Float32Array(maxOut * 2);
        g1.set(this.outAccum);
        this.outAccum = g1;
        const g2 = new Float32Array(maxOut * 2);
        g2.set(this.outNorm);
        this.outNorm = g2;
      }

      // ── 3. Process one hop at a time ────────────────────────────────────────
      // Each hop advances by HOP samples. We need ≥1 new sample at `offset`
      // to constitute new audio; the rest of the N_FFT window is zero-padded.
      let offset = 0;
      while (offset + HOP <= this.inRingLen) {
        // Build one windowed N_FFT frame (zero-pad beyond ring buffer)
        const frame = new Float32Array(N_FFT);
        for (let i = 0; i < N_FFT; i++) {
          const idx = offset + i;
          frame[i] = (idx < this.inRingLen ? this.inRing[idx] : 0) * hann[i];
        }

        // FFT
        const {real: fR, imag: fI} = MLNoiseSuppressor.fftForward(frame);

        // Mel features for this hop — push to rolling history
        const power = new Float32Array(bins);
        for (let k = 0; k < bins; k++) power[k] = fR[k] * fR[k] + fI[k] * fI[k];
        const melFrame = this.computeMelFrame(power, N_MELS);
        this.melFrameHistory.push(melFrame);
        if (this.melFrameHistory.length > SEQ_LEN)
          this.melFrameHistory = this.melFrameHistory.slice(-SEQ_LEN);

        // Apply per-bin gains (all-ones = transparent until first inference)
        const gains = this.cachedFftGains ?? new Float32Array(bins).fill(1.0);
        const mR = new Float32Array(N_FFT);
        const mI = new Float32Array(N_FFT);
        for (let k = 0; k < bins; k++) {
          mR[k] = fR[k] * gains[k];
          mI[k] = fI[k] * gains[k];
          if (k > 0 && k < N_FFT / 2) {
            // mirror conjugate symmetry
            mR[N_FFT - k] = mR[k];
            mI[N_FFT - k] = -mI[k];
          }
        }

        // ISTFT → time domain frame
        const recon = MLNoiseSuppressor.ifftFromComplex(mR, mI);

        // Overlap-add into output accumulator at this hop's position
        for (let i = 0; i < N_FFT; i++) {
          const pos = offset + i;
          this.outAccum[pos] += recon[i] * hann[i]; // synthesis Hann window
          this.outNorm[pos] += hann[i] * hann[i]; // OLA normalisation envelope
        }

        // Fire async inference every hop (non-blocking; inferenceInFlight prevents overlap)
        if (!this.inferenceInFlight) {
          this.runInferenceAsync(this.melFrameHistory.slice(), SEQ_LEN, N_MELS);
        }

        offset += HOP;
      }

      // After the loop, positions 0..offset-1 are fully covered by all overlapping hops
      // that will ever write to them. Read those samples, normalise, and output them.

      // ── 4. Read `in16.length` output samples ────────────────────────────────
      const out16 = new Float32Array(in16.length);
      const ready = Math.min(in16.length, offset); // offset = # samples fully processed
      for (let i = 0; i < ready; i++) {
        out16[i] =
          this.outNorm[i] > 1e-10
            ? this.outAccum[i] / this.outNorm[i]
            : in16[i]; // no coverage → raw passthrough
      }
      // Any samples beyond `ready` (rare, only first few callbacks) → raw passthrough
      for (let i = ready; i < in16.length; i++) out16[i] = in16[i];

      // ── 5. Advance ring buffers ──────────────────────────────────────────────
      // Discard the `offset` processed input samples; shift output accumulators so
      // any partial OLA contributions from the last N_FFT window carry over correctly.
      this.inRing.copyWithin(0, offset);
      this.inRingLen -= offset;
      this.outAccum.copyWithin(0, offset);
      this.outNorm.copyWithin(0, offset);
      // Zero-fill the freed tail (so stale values don't corrupt future hops)
      this.outAccum.fill(0, this.inRingLen + N_FFT);
      this.outNorm.fill(0, this.inRingLen + N_FFT);

      // ── 6. Upsample 16kHz → 48kHz ───────────────────────────────────────────
      return this.needsResampling
        ? this.upsample(out16, this.resampleRatio, inputBuffer.length)
        : out16;
    } catch {
      return inputBuffer;
    }
  }

  /**
   * Compute mel-spectrogram features for one STFT frame.
   * Matches training pipeline: power_to_db(ref=max) → (db+80)/80 clipped [0,1].
   */
  private computeMelFrame(powerSpec: Float32Array, N_MELS: number): number[] {
    if (!this.config) return Array(N_MELS).fill(0);
    const N_FFT = this.config.n_fft || 512;
    const SR = this.config.sample_rate || 16000;
    if (!this.melFilterbank)
      this.melFilterbank = MLNoiseSuppressor.buildMelFilterbank(
        N_MELS,
        N_FFT,
        SR
      );

    const melPow = new Float32Array(N_MELS);
    for (let m = 0; m < N_MELS; m++) {
      let s = 0;
      const filt = this.melFilterbank![m];
      for (let k = 0; k < filt.length && k < powerSpec.length; k++)
        s += filt[k] * powerSpec[k];
      melPow[m] = s;
    }
    let maxP = 1e-10;
    for (let m = 0; m < N_MELS; m++) if (melPow[m] > maxP) maxP = melPow[m];
    const refDb = 10 * Math.log10(maxP);
    const out = new Array<number>(N_MELS);
    for (let m = 0; m < N_MELS; m++) {
      const db = 10 * Math.log10(melPow[m] + 1e-10) - refDb;
      out[m] = Math.max(0, Math.min(1, (db + 80) / 80));
    }
    return out;
  }

  /**
   * Run model inference asynchronously.
   * Updates cachedFftGains (per-FFT-bin gains) — audio thread applies them each hop.
   * Never awaited from processAudioSync; inferenceInFlight prevents overlapping calls.
   */
  private async runInferenceAsync(
    historySnapshot: number[][],
    SEQ_LEN: number,
    N_MELS: number
  ): Promise<void> {
    this.inferenceInFlight = true;
    try {
      // Build [1, SEQ_LEN, N_MELS] input tensor, zero-padded at start
      const histLen = historySnapshot.length;
      const padCount = SEQ_LEN - histLen;
      const flat = new Array(SEQ_LEN * N_MELS).fill(0);
      for (let f = 0; f < histLen; f++)
        for (let m = 0; m < N_MELS; m++)
          flat[(padCount + f) * N_MELS + m] = historySnapshot[f][m];

      const N_FFT = this.config!.n_fft || 512;
      const SR = this.config!.sample_rate || 16000;
      const bins = N_FFT / 2 + 1;

      const inp = tf.tensor(flat, [1, SEQ_LEN, N_MELS]) as tf.Tensor3D;
      const out = this.model!.predict(inp) as tf.Tensor;

      // .data() is async — NEVER blocks the audio thread
      const maskFlat = new Float32Array(await out.data());
      inp.dispose();
      out.dispose();

      // Use the LAST row of [SEQ_LEN, N_MELS] output = mask for the most recent frame
      const mask40 = new Float32Array(N_MELS);
      for (let m = 0; m < N_MELS; m++)
        mask40[m] = maskFlat[(SEQ_LEN - 1) * N_MELS + m];

      // Temporal smoothing
      const smoothed = this.applyTemporalSmoothing(mask40);

      // Map mel-band mask → per-FFT-bin gains via transpose filterbank
      if (!this.melFilterbank)
        this.melFilterbank = MLNoiseSuppressor.buildMelFilterbank(
          N_MELS,
          N_FFT,
          SR
        );

      const gains = new Float32Array(bins);
      const filtTotal = new Float32Array(bins);
      for (let m = 0; m < N_MELS; m++) {
        const filt = this.melFilterbank![m];
        for (let k = 0; k < filt.length && k < bins; k++) {
          gains[k] += filt[k] * smoothed[m];
          filtTotal[k] += filt[k];
        }
      }
      for (let k = 0; k < bins; k++) {
        // No mel coverage → preserve (DC, Nyquist)
        gains[k] = filtTotal[k] > 1e-8 ? gains[k] / filtTotal[k] : 1.0;
        // Floor: 0.0 → let model fully suppress noise. Voice protected by model itself.
        gains[k] = Math.max(0.0, Math.min(1.0, gains[k]));
      }

      this.cachedFftGains = gains;
    } catch {
      // Keep last good gains
    } finally {
      this.inferenceInFlight = false;
    }
  }

  /**
   * Downsample audio buffer (e.g., 48kHz → 16kHz)
   * Simple linear interpolation for speed
   */
  private downsample(input: Float32Array, ratio: number): Float32Array {
    const outputLength = Math.floor(input.length / ratio);
    const output = new Float32Array(outputLength);

    for (let i = 0; i < outputLength; i++) {
      const srcIndex = i * ratio;
      const srcIndexFloor = Math.floor(srcIndex);
      const frac = srcIndex - srcIndexFloor;

      if (srcIndexFloor + 1 < input.length) {
        // Linear interpolation between samples
        output[i] =
          input[srcIndexFloor] * (1 - frac) + input[srcIndexFloor + 1] * frac;
      } else {
        output[i] = input[srcIndexFloor];
      }
    }

    return output;
  }

  /**
   * Upsample audio buffer (e.g., 16kHz → 48kHz)
   * Linear interpolation to match original length
   */
  private upsample(
    input: Float32Array,
    ratio: number,
    targetLength: number
  ): Float32Array {
    const output = new Float32Array(targetLength);

    for (let i = 0; i < targetLength; i++) {
      const srcIndex = i / ratio;
      const srcIndexFloor = Math.floor(srcIndex);
      const frac = srcIndex - srcIndexFloor;

      if (srcIndexFloor + 1 < input.length) {
        // Linear interpolation between samples
        output[i] =
          input[srcIndexFloor] * (1 - frac) + input[srcIndexFloor + 1] * frac;
      } else if (srcIndexFloor < input.length) {
        output[i] = input[srcIndexFloor];
      } else {
        output[i] = input[input.length - 1];
      }
    }

    return output;
  }

  /**
   * Extract mel-spectrogram features matching the training pipeline exactly:
   *   librosa.feature.melspectrogram(n_fft, hop_length, n_mels) — values from config
   *   → power_to_db(ref=np.max)
   *   → (mel_db + 80) / 80  clipped to [0, 1]
   * No z-score normalisation applied (model trained on raw [0,1] values).
   *
   * Config driven: works with 16kHz/40mels or 48kHz/128mels depending on model_config.json
   */
  private extractFeatures(audio: Float32Array): number[][] {
    if (!this.config) return [[Array(128).fill(0)][0]];

    const N_FFT = this.config?.n_fft || 2048;
    const HOP = this.config.hop_length || 256;
    const N_MELS = this.config.n_mels || 128;
    const SR = this.config.sample_rate || 48000;

    // Build (and cache) the mel filterbank once
    if (!this.melFilterbank) {
      this.melFilterbank = MLNoiseSuppressor.buildMelFilterbank(
        N_MELS,
        N_FFT,
        SR
      );
    }

    // Pre-compute Hann window
    const hannWindow = new Float32Array(N_FFT);
    for (let i = 0; i < N_FFT; i++) {
      hannWindow[i] = 0.5 * (1 - Math.cos((2 * Math.PI * i) / (N_FFT - 1)));
    }

    const numFrames = Math.max(1, Math.floor((audio.length - N_FFT) / HOP) + 1);
    const features: number[][] = [];
    this.lastComplexFrames = []; // reset for this buffer

    for (let fi = 0; fi < numFrames; fi++) {
      const start = fi * HOP;

      // Apply Hann window to this frame (zero-pad if near end)
      const frame = new Float32Array(N_FFT);
      for (let i = 0; i < N_FFT; i++) {
        frame[i] =
          (start + i < audio.length ? audio[start + i] : 0) * hannWindow[i];
      }

      // FFT → complex spectrum (saved for OLA reconstruction) + power spectrum
      const {real: fftReal, imag: fftImag} =
        MLNoiseSuppressor.fftForward(frame);
      const bins = N_FFT / 2 + 1;
      this.lastComplexFrames.push({
        real: fftReal.slice(0, bins),
        imag: fftImag.slice(0, bins),
      });
      const powerSpec = new Float32Array(bins);
      for (let k = 0; k < bins; k++) {
        powerSpec[k] = fftReal[k] * fftReal[k] + fftImag[k] * fftImag[k];
      }

      // Apply mel filterbank → mel power per band
      const melPower = new Float32Array(N_MELS);
      for (let m = 0; m < N_MELS; m++) {
        let sum = 0;
        const filt = this.melFilterbank![m];
        for (let k = 0; k < filt.length; k++) sum += filt[k] * powerSpec[k];
        melPower[m] = sum;
      }

      // power_to_db(ref=np.max): 10*log10(S/max(S))  — matches librosa default
      let maxPow = 1e-10;
      for (let m = 0; m < N_MELS; m++)
        if (melPower[m] > maxPow) maxPow = melPower[m];
      const refDb = 10 * Math.log10(maxPow);

      // (mel_db + 80) / 80, clipped to [0, 1] — matches training normalisation
      const melNorm = new Array<number>(N_MELS);
      for (let m = 0; m < N_MELS; m++) {
        const db = 10 * Math.log10(melPower[m] + 1e-10) - refDb;
        melNorm[m] = Math.max(0, Math.min(1, (db + 80) / 80));
      }

      features.push(melNorm);
    }

    return features;
  }

  /**
   * Cooley-Tukey radix-2 FFT — returns full complex spectrum.
   * Accepts optional imaginary input for use as IFFT building block.
   * frameReal.length must be a power of 2.
   */
  private static fftForward(
    frameReal: Float32Array,
    frameImag?: Float32Array
  ): {real: Float32Array; imag: Float32Array} {
    const N = frameReal.length;
    const real = new Float32Array(frameReal);
    const imag = frameImag ? new Float32Array(frameImag) : new Float32Array(N);

    // Bit-reversal permutation
    let j = 0;
    for (let i = 1; i < N; i++) {
      let bit = N >> 1;
      while (j & bit) {
        j ^= bit;
        bit >>= 1;
      }
      j ^= bit;
      if (i < j) {
        let tmp = real[i];
        real[i] = real[j];
        real[j] = tmp;
        tmp = imag[i];
        imag[i] = imag[j];
        imag[j] = tmp;
      }
    }

    // Butterfly passes
    for (let len = 2; len <= N; len <<= 1) {
      const halfLen = len >> 1;
      const ang = (-2 * Math.PI) / len;
      const wRe = Math.cos(ang);
      const wIm = Math.sin(ang);
      for (let i = 0; i < N; i += len) {
        let curRe = 1,
          curIm = 0;
        for (let k = 0; k < halfLen; k++) {
          const uRe = real[i + k],
            uIm = imag[i + k];
          const vRe =
            real[i + k + halfLen] * curRe - imag[i + k + halfLen] * curIm;
          const vIm =
            real[i + k + halfLen] * curIm + imag[i + k + halfLen] * curRe;
          real[i + k] = uRe + vRe;
          imag[i + k] = uIm + vIm;
          real[i + k + halfLen] = uRe - vRe;
          imag[i + k + halfLen] = uIm - vIm;
          const newRe = curRe * wRe - curIm * wIm;
          curIm = curRe * wIm + curIm * wRe;
          curRe = newRe;
        }
      }
    }
    return {real, imag};
  }

  /**
   * IFFT via conjugate trick: ifft(X) = conj(fft(conj(X))) / N
   * Returns the real part of the time-domain signal.
   */
  private static ifftFromComplex(
    real: Float32Array,
    imag: Float32Array
  ): Float32Array {
    const N = real.length;
    // Conjugate input: negate imaginary part
    const conjImag = new Float32Array(N);
    for (let i = 0; i < N; i++) conjImag[i] = -imag[i];
    // FFT of conjugate
    const {real: outReal, imag: outImag} = MLNoiseSuppressor.fftForward(
      real,
      conjImag
    );
    // Conjugate result and scale by 1/N — take real part (output is real-valued)
    const output = new Float32Array(N);
    for (let i = 0; i < N; i++) {
      output[i] = outReal[i] / N; // conj(outReal+j*outImag)/N → real part = outReal/N
    }
    return output;
  }

  /**
   * Build an N-band triangular mel filterbank matching librosa's defaults.
   * Uses Slaney mel scale (htk=False, librosa default): linear below 1 kHz,
   * logarithmic above. This matches librosa.feature.melspectrogram used in
   * Colab training — HTK formula would produce different band-frequency
   * assignments and degrade suppression quality.
   */
  static buildMelFilterbank(
    nMels: number,
    nFft: number,
    sr: number
  ): Float32Array[] {
    const bins = nFft / 2 + 1;
    // Slaney mel scale — matches librosa htk=False default
    const F_SP = 200.0 / 3; // Hz per linear mel (~66.67)
    const MIN_LOG_HZ = 1000.0; // breakpoint
    const MIN_LOG_MEL = MIN_LOG_HZ / F_SP; // ≈15.0
    const LOGSTEP = Math.log(6.4) / 27.0; // log step above breakpoint
    const hzToMel = (hz: number) =>
      hz < MIN_LOG_HZ
        ? hz / F_SP
        : MIN_LOG_MEL + Math.log(hz / MIN_LOG_HZ) / LOGSTEP;
    const melToHz = (mel: number) =>
      mel < MIN_LOG_MEL
        ? mel * F_SP
        : MIN_LOG_HZ * Math.exp(LOGSTEP * (mel - MIN_LOG_MEL));

    const melMin = hzToMel(0);
    const melMax = hzToMel(sr / 2);

    // nMels + 2 equally-spaced mel points covering [0, sr/2]
    const melPts = new Float32Array(nMels + 2);
    for (let i = 0; i < nMels + 2; i++) {
      melPts[i] = melToHz(melMin + (i / (nMels + 1)) * (melMax - melMin));
    }

    // FFT bin centre frequencies
    const fftFreqs = new Float32Array(bins);
    for (let k = 0; k < bins; k++) fftFreqs[k] = (k * sr) / nFft;

    const filters: Float32Array[] = [];
    for (let m = 0; m < nMels; m++) {
      const filt = new Float32Array(bins);
      const left = melPts[m];
      const center = melPts[m + 1];
      const right = melPts[m + 2];
      for (let k = 0; k < bins; k++) {
        const f = fftFreqs[k];
        if (f >= left && f <= center) {
          filt[k] = (f - left) / (center - left + 1e-10);
        } else if (f > center && f <= right) {
          filt[k] = (right - f) / (right - center + 1e-10);
        }
      }
      filters.push(filt);
    }
    return filters;
  }

  /**
   * Apply temporal smoothing to the 40-band mel mask.
   *
   * Standard IRM smoothing: alpha weighted toward the PREVIOUS mask (heavy weight)
   * to prevent "musical noise" — rapid per-band gain changes that create tonal
   * artifacts. SMOOTHING_ALPHA=0.85 means 85% previous + 15% new each frame.
   *
   * BUG FIX: the old code had `alpha * current + (1-alpha) * prev` which was
   * effectively 85% tracking the current mask — almost no smoothing, causing chirps.
   */
  private applyTemporalSmoothing(
    currentMask: Float32Array,
    participantId: string = "__default__"
  ): Float32Array {
    const smoothed = new Float32Array(currentMask.length);
    const prev = this.prevMaskMap.get(participantId);

    if (!prev || prev.length !== currentMask.length) {
      const init = new Float32Array(currentMask);
      this.prevMaskMap.set(participantId, init);
      return init;
    }

    for (let i = 0; i < currentMask.length; i++) {
      // Heavy weight on PREVIOUS mask for smooth transitions — no pumping artefacts
      smoothed[i] =
        this.SMOOTHING_ALPHA * prev[i] +
        (1 - this.SMOOTHING_ALPHA) * currentMask[i];
      // NO floor — let the model fully suppress noise bins (IRM ≈ 0).
      // A 0.3 floor forced ALL bins (including pure noise) to 30% gain,
      // causing residual noise bleed-through and overall quiet/muffled audio.
      // Voice protection comes from the model itself (trained IRM ≈ 1.0 for voice bands).
      smoothed[i] = Math.max(0.0, Math.min(1.0, smoothed[i]));
    }

    this.prevMaskMap.set(participantId, smoothed);
    return smoothed;
  }

  /**
   * Apply the 40-band spectral mask via STFT → gain → ISTFT (overlap-add).
   *
   * Replaces the old applyMaskToAudio which incorrectly used frequency-domain mask
   * values as time-domain sample gains — producing distortion rather than clean
   * suppression. Correct approach:
   *   1. Map 40-band mel mask → per-FFT-bin gain via transpose mel filterbank
   *   2. Apply gains to the saved complex STFT frames
   *   3. Reconstruct via synthesis Hann window + overlap-add
   */
  private applyMaskViaOLA(
    audio: Float32Array,
    mask40: Float32Array,
    N_FFT: number,
    HOP: number,
    N_MELS: number
  ): Float32Array {
    // Fallback: if STFT frames weren't saved, pass audio through unchanged
    if (!this.melFilterbank || this.lastComplexFrames.length === 0) {
      return audio;
    }

    const bins = N_FFT / 2 + 1;

    // ── Map 40-mel mask → per-FFT-bin gain via transpose filterbank ──────────
    // For each STFT bin k: gain[k] = (∑m filters[m][k] × mask[m]) / (∑m filters[m][k])
    // Bins with no mel coverage keep gain=1.0 (DC, Nyquist — don't suppress).
    const fftGains = new Float32Array(bins);
    const filtTotals = new Float32Array(bins);
    for (let m = 0; m < N_MELS; m++) {
      const filt = this.melFilterbank[m];
      for (let k = 0; k < filt.length && k < bins; k++) {
        fftGains[k] += filt[k] * mask40[m];
        filtTotals[k] += filt[k];
      }
    }
    for (let k = 0; k < bins; k++) {
      fftGains[k] = filtTotals[k] > 1e-8 ? fftGains[k] / filtTotals[k] : 1.0; // no mel coverage → preserve
      fftGains[k] = Math.max(0.3, Math.min(1.0, fftGains[k])); // floor 0.3 — voice never suppressed below 30%
    }

    // Synthesis Hann window (= analysis window; guarantees perfect reconstruction at 50% overlap)
    const hannWindow = new Float32Array(N_FFT);
    for (let i = 0; i < N_FFT; i++) {
      hannWindow[i] = 0.5 * (1 - Math.cos((2 * Math.PI * i) / (N_FFT - 1)));
    }

    const output = new Float32Array(audio.length);
    const normFactor = new Float32Array(audio.length);

    for (let fi = 0; fi < this.lastComplexFrames.length; fi++) {
      const start = fi * HOP;
      const {real: origReal, imag: origImag} = this.lastComplexFrames[fi];

      // Build full N_FFT complex arrays with conjugate symmetry applied
      const maskedReal = new Float32Array(N_FFT);
      const maskedImag = new Float32Array(N_FFT);
      for (let k = 0; k < bins; k++) {
        maskedReal[k] = origReal[k] * fftGains[k];
        maskedImag[k] = origImag[k] * fftGains[k];
        // Mirror conjugate for negative frequencies (skip DC k=0 and Nyquist k=N_FFT/2)
        if (k > 0 && k < N_FFT / 2) {
          maskedReal[N_FFT - k] = maskedReal[k];
          maskedImag[N_FFT - k] = -maskedImag[k]; // conjugate: negate imag
        }
      }

      // IFFT → time-domain frame
      const reconstructed = MLNoiseSuppressor.ifftFromComplex(
        maskedReal,
        maskedImag
      );

      // Overlap-add with synthesis window
      for (let i = 0; i < N_FFT; i++) {
        const pos = start + i;
        if (pos < audio.length) {
          output[pos] += reconstructed[i] * hannWindow[i];
          normFactor[pos] += hannWindow[i] * hannWindow[i];
        }
      }
    }

    // Normalise by OLA envelope; fall back to original sample where no coverage
    for (let i = 0; i < output.length; i++) {
      output[i] = normFactor[i] > 1e-10 ? output[i] / normFactor[i] : audio[i];
    }

    return output;
  }

  /**
   * Reset processing state (call when participant reconnects or audio track resets)
   */
  reset(): void {
    this.prevMaskMap.clear();
    this.melFrameHistory = [];
    this.lastComplexFrames = [];
    // Ring buffer state
    this.inRing = new Float32Array(8192);
    this.inRingLen = 0;
    this.outAccum = new Float32Array(16384);
    this.outNorm = new Float32Array(16384);
    this.cachedFftGains = null;
    this.inferenceInFlight = false;
  }

  /**
   * Check if ML processor is ready
   */
  isReady(): boolean {
    return this.isInitialized && this.model !== null;
  }

  /**
   * Return the loaded model config so callers (e.g. AudioWorkletNode setup)
   * can read n_fft, hop_length, n_mels, sample_rate, sequence_length.
   */
  getModelConfig(): ModelConfig | null {
    return this.config;
  }

  /**
   * Run one inference pass from pre-computed mel features and return per-FFT-bin
   * gains. Called by SpatialAudioChannel for each AudioWorkletNode message.
   *
   * @param features  Flat Float32Array of shape [SEQ_LEN × N_MELS] (row-major).
   *                  Produced by the AudioWorklet processor and transferred via postMessage.
   * @param seqLen    Sequence length (default from config).
   * @param nMels     Number of mel bands (default from config).
   * @returns         Float32Array of length N_FFT/2+1 with per-bin gains in [0,1].
   */
  async computeGainsFromFeatures(
    features: Float32Array,
    seqLen: number,
    nMels: number,
    participantId: string = "__default__"
  ): Promise<Float32Array> {
    if (!this.model || !this.config) {
      const bins = (this.config?.n_fft || 512) / 2 + 1;
      return new Float32Array(bins).fill(1.0);
    }

    const N_FFT = this.config.n_fft || 512;
    const SR = this.config.sample_rate || 16000;
    const bins = N_FFT / 2 + 1;

    try {
      // Features arrive from the AudioWorklet already in [0, 1] range via:
      //   (power_to_db(ref=max) + 80) / 80  — identical to the training pipeline.
      // The model was trained on raw [0, 1] features WITHOUT z-score normalization.
      // (normalization_stats.json describes the data distribution but was NOT
      //  applied to X before training in Colab.) Applying z-score here would
      //  shift inputs to [-3, +3] and produce garbage gains.
      const inp = tf.tensor(Array.from(features), [
        1,
        seqLen,
        nMels,
      ]) as tf.Tensor3D;
      const out = this.model.predict(inp) as tf.Tensor;
      const maskFlat = new Float32Array(await out.data());
      inp.dispose();
      out.dispose();

      // ── Step 1: Extract last frame's raw mel IRM (sigmoid output, [0,1]) ───
      const rawMask = new Float32Array(nMels);
      for (let m = 0; m < nMels; m++) {
        rawMask[m] = Math.max(
          0,
          Math.min(1, maskFlat[(seqLen - 1) * nMels + m])
        );
      }

      // ── Step 2: Detect speech from RAW model output — BEFORE smoothing ──────
      //
      // CRITICAL FIX: isSpeechFrame must use the raw model output, NOT the
      // temporally-smoothed gains. With SMOOTHING_ALPHA=0.92 (old value), the
      // first speech frame produced smoothed max ≈ 0.08, which fell below the
      // 0.10 threshold → isSpeechFrame=false → gains=0 → first ~200ms of every
      // utterance was SILENCED. Using raw output (typically max=1.0 for speech)
      // ensures the gate opens on the very first speech frame.
      let rawMax = 0;
      for (let m = 0; m < nMels; m++) {
        if (rawMask[m] > rawMax) rawMax = rawMask[m];
      }

      // Safety: if model outputs near-zero (weight loading failure, warmup,
      // or feature mismatch), return full passthrough so the worklet gate stays
      // open and audio is heard. rawMax=0.000 in logs usually means the model
      // is outputting ~0.0001-0.003 (rounds to 0.000 at 3dp) — caught here.
      if (rawMax < 0.005) {
        // ⚠️ LOG THIS — silent passthrough was the root cause of "fan noise always heard".
        // If you see this frequently, weights are not loaded (GRU cell naming mismatch).
        const passthroughCount =
          (MLNoiseSuppressor as any)._passthroughCount || 0;
        (MLNoiseSuppressor as any)._passthroughCount = passthroughCount + 1;
        const lastPassthroughLog =
          (MLNoiseSuppressor as any)._lastPassthroughLog || 0;
        if (Date.now() - lastPassthroughLog > 2000) {
          (MLNoiseSuppressor as any)._lastPassthroughLog = Date.now();
          console.warn(
            `[ML] ❌ PASSTHROUGH (rawMax=${rawMax.toFixed(5)}) — model output near-zero. ` +
              `Count since last log: ${passthroughCount}. ` +
              `Weights likely NOT loaded. Check [MLNoiseSuppressor] weight name dump above.`
          );
          (MLNoiseSuppressor as any)._passthroughCount = 0;
        }
        const passthrough = new Float32Array(bins).fill(1.0);
        return passthrough;
      }

      // Threshold 0.108: fan/AC noise consistently scores rawMax=0.094-0.100.
      // Speech consistently scores rawMax≥0.111. Gap between 0.100 and 0.111 gives
      // 8% margin. Previously 0.10 caused fan frames scoring exactly 0.100 to
      // be classified as speech → fan noise leaked through during silence.
      let isSpeechFrame = rawMax >= 0.108;

      // ── Speech holdover: bridge within-word noise frames ─────────────────
      // The model scores fricatives ("s","f"), plosives ("t","k","p") and
      // vowel transitions below threshold even mid-utterance, causing rapid
      // speech/noise alternation in the gains signal. A short holdover of
      // SF_HOLDOVER frames (~40ms) keeps isSpeechFrame=true for a few frames
      // after the last detected speech frame, smoothing out the chatter.
      const SF_HOLDOVER = 8; // 8 hops × ~5ms = ~40ms
      const sfMap: Map<string, number> =
        (MLNoiseSuppressor as any)._sfHoldoverMap ||
        ((MLNoiseSuppressor as any)._sfHoldoverMap = new Map());
      const sfHold = sfMap.get(participantId) || 0;
      if (isSpeechFrame) {
        sfMap.set(participantId, SF_HOLDOVER);
      } else if (sfHold > 0) {
        isSpeechFrame = true; // bridge: still within holdover window
        sfMap.set(participantId, sfHold - 1);
      } else {
        sfMap.set(participantId, 0);
      }

      // ── Step 3: Temporal smoothing on mel mask ────────────────────────────
      // Smoothing happens AFTER speech detection so it only affects gain
      // magnitude (fade-in/out), not the binary speech/noise decision.
      const smoothed = this.applyTemporalSmoothing(rawMask, participantId);

      // ── Step 4: Map 40 mel IRM → 257 FFT bins via transpose filterbank ────
      if (!this.melFilterbank) {
        this.melFilterbank = MLNoiseSuppressor.buildMelFilterbank(
          nMels,
          N_FFT,
          SR
        );
      }
      const gains = new Float32Array(bins);
      const filtTotal = new Float32Array(bins);
      for (let m = 0; m < nMels; m++) {
        const filt = this.melFilterbank[m];
        for (let k = 0; k < filt.length && k < bins; k++) {
          gains[k] += filt[k] * smoothed[m];
          filtTotal[k] += filt[k];
        }
      }
      for (let k = 0; k < bins; k++) {
        gains[k] = filtTotal[k] > 1e-8 ? gains[k] / filtTotal[k] : 1.0;
        gains[k] = Math.max(0.0, Math.min(1.0, gains[k]));
      }

      // ── Step 5: Speech-band protection floor ──────────────────────────────
      // Logs showed removed=7–9dB with floor=0.20: fan bins (model IRM≈0.05)
      // were being held at 0.20 = −14dB passthrough. Suppressed level was still
      // -47dBFS → still audible. Need -60dBFS+.
      //
      // Lower floor to 0.05: fan bins go 0.20→0.05 = −26dB on those bins.
      // Voice bins (model IRM≈0.85) are unaffected — well above either floor.
      // Effect:
      //   Speech fundamental (raw IRM≈0.86) → 0.86  (unchanged, full voice)
      //   Speech formants    (raw IRM≈0.65) → 0.65  (unchanged, full voice)
      //   Mixed fan+speech   (raw IRM≈0.25) → 0.25  (−12dB, acceptable)
      //   Pure fan bins      (raw IRM≈0.05) → 0.05  (−26dB, near-inaudible)
      // Expected: removed increases from ~8dB → ~18dB.
      // During noise frames: no floor — model suppresses to full silence.
      const IRM_SPEECH_FLOOR = 0.05;
      if (isSpeechFrame) {
        for (let k = 1; k < bins; k++) {
          gains[k] = Math.max(IRM_SPEECH_FLOOR, gains[k]);
        }
      }

      // ── Step 6: Override gains[0] as explicit gate signal ────────────────
      // The DC bin (k=0) has no mel filter coverage → filtTotal[0] ≤ 1e-8 →
      // gains[0] defaults to 1.0 regardless of the model's output. The worklet
      // reads gains[0] as its ML gate: if it's always 1.0, ML never closes the
      // gate and background noise leaks through during speech. Override it to
      // the binary speech decision so the worklet gate properly tracks ML output.
      gains[0] = isSpeechFrame ? 1.0 : 0.0;

      // ── Diagnostic log ────────────────────────────────────────────────────
      const now = Date.now();
      const lastLog = (MLNoiseSuppressor as any)._lastGainLog || new Map();
      const lastT = lastLog.get(participantId) || 0;
      if (!isSpeechFrame || now - lastT > 1000) {
        lastLog.set(participantId, now);
        (MLNoiseSuppressor as any)._lastGainLog = lastLog;
        // suppression% = how much energy the ML removed (0%=passthrough, 100%=silence)
        let gainSum = 0;
        for (let k = 1; k < bins; k++) gainSum += gains[k];
        const meanIRM = gainSum / (bins - 1);
        const suppPct = ((1 - meanIRM) * 100).toFixed(0);
        console.log(
          `[ML] ${isSpeechFrame ? "🎙️ SPEECH" : "🔇 NOISE "} p=${participantId.substring(0, 8)} rawMax=${rawMax.toFixed(3)} suppression=${suppPct}% gate=${gains[0].toFixed(0)}`
        );
      }

      return gains;
    } catch {
      return new Float32Array(bins).fill(1.0);
    }
  }

  /**
   * Get model info
   */
  getInfo(): {initialized: boolean; backend: string; modelLoaded: boolean} {
    return {
      initialized: this.isInitialized,
      backend: tf.getBackend(),
      modelLoaded: this.model !== null,
    };
  }

  /**
   * Remove per-participant smoothing state when a participant disconnects.
   * Keeps prevMaskMap from growing unboundedly in long sessions.
   */
  clearParticipantSmoothingState(participantId: string): void {
    this.prevMaskMap.delete(participantId);
  }

  /**
   * Cleanup resources
   */
  dispose(): void {
    if (this.model) {
      this.model.dispose();
      this.model = null;
    }
    this.prevMaskMap.clear();
    this.isInitialized = false;
  }
}
